<script setup>
import { ref, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  ticket: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'resolved']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const resolutionNotes = ref('');
const isResolving = ref(false);

const onClose = () => {
  emit('close');
  resolutionNotes.value = '';
};

const resolveTicket = async () => {
  isResolving.value = true;
  try {
    await store.dispatch('tickets/resolveTicket', {
      id: props.ticket.display_id,
      resolutionNotes: resolutionNotes.value,
    });
    useAlert(t('TICKETS.RESOLVED_SUCCESS'));
    emit('resolved');
    dialogRef.value?.close();
    resolutionNotes.value = '';
  } catch (error) {
    useAlert(t('TICKETS.RESOLVE_ERROR'));
  } finally {
    isResolving.value = false;
  }
};

onMounted(() => {
  if (props.show) {
    dialogRef.value?.open();
  }
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="$t('TICKETS.RESOLVE_MODAL.TITLE')"
    :is-loading="isResolving"
    :confirm-button-label="$t('TICKETS.RESOLVE_MODAL.CONFIRM')"
    :cancel-button-label="$t('TICKETS.FORM.CANCEL')"
    @confirm="resolveTicket"
    @close="onClose"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKETS.RESOLVE_MODAL.NOTES') }}
        </label>
        <textarea
          v-model="resolutionNotes"
          :placeholder="$t('TICKETS.RESOLVE_MODAL.NOTES_PLACEHOLDER')"
          rows="5"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
        />
      </div>
    </div>
  </Dialog>
</template>
